import { Fragment, useEffect, useState, useCallback } from "react";
import { H2, Body, Link } from "@leafygreen-ui/typography";
import Banner from "@leafygreen-ui/banner";
import { css } from "@leafygreen-ui/emotion";

import { useApi } from "../../providers/Api";
import CreateInviteForm from "./CreateInviteForm";

const section = css`
  margin-top: 32px;
  max-width: 720px;
`;

const largerText = css`
  font-size: 18px;
`;

export default function InstruqtInvites() {
  const api = useApi();
  const [tracks, setTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [tracksError, setTracksError] = useState(null);
  const [manageInvitesUrl, setManageInvitesUrl] = useState(null);
  const [organizationSlug, setOrganizationSlug] = useState(null);
  const [environment, setEnvironment] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [internalOnlyEnvironments, setInternalOnlyEnvironments] = useState([]);
  const [manageInvitesError, setManageInvitesError] = useState(null);

  const loadTracks = useCallback(async (env) => {
    setTracksLoading(true);
    try {
      const result = await api.getInstruqtTracks(env);
      if (result?.error) {
        console.error(
          `[Instruqt Invites] Failed to load tracks: ${result.error}\n` +
          "If this mentions missing config, check that this environment's " +
          "INSTRUQT_<ENV>_API_KEY / INSTRUQT_<ENV>_TEAM_NAME env vars are set on the " +
          "api service (docker-compose.yaml/.env locally, Cloud Run --set-secrets in production)."
        );
        setTracksError(result.error);
      } else if (!Array.isArray(result)) {
        console.error("[Instruqt Invites] Expected an array of tracks from the API, got:", result);
        setTracksError("Could not load tracks (unexpected response from the server)");
      } else {
        setTracks(result);
        setTracksError(null);
      }
    } catch (e) {
      console.error("[Instruqt Invites] Failed to load tracks:", e);
      setTracksError("Could not load tracks");
    }
    setTracksLoading(false);
  }, [api]);

  const loadManageInvitesUrl = useCallback(async (env) => {
    try {
      const result = await api.getInstruqtManageInvitesUrl(env);
      if (result?.error) {
        console.error(`[Instruqt Invites] Failed to load the Instruqt invites link: ${result.error}`);
        setManageInvitesError(result.error);
      } else {
        setManageInvitesUrl(result.manageInvitesUrl);
        setOrganizationSlug(result.organizationSlug);
        setEnvironment(result.environment);
        setEnvironments(result.environments || []);
        setInternalOnlyEnvironments(result.internalOnlyEnvironments || []);
        setManageInvitesError(null);
      }
    } catch (e) {
      console.error("[Instruqt Invites] Failed to load the Instruqt invites link:", e);
      setManageInvitesError("Could not load the Instruqt invites link");
    }
  }, [api]);

  useEffect(() => {
    loadTracks();
    loadManageInvitesUrl();
  }, [loadTracks, loadManageInvitesUrl]);

  const handleEnvironmentChange = (newEnvironment) => {
    setEnvironment(newEnvironment);
    loadTracks(newEnvironment);
    loadManageInvitesUrl(newEnvironment);
  };

  const handleDryRun = (payload) => api.createInstruqtInvite({ ...payload, environment, dryRun: true });

  const handleCreate = (payload) => api.createInstruqtInvite({ ...payload, environment, dryRun: false });

  return (
    <Fragment>
      <H2>Instruqt Invites</H2>
      <Body>Create a live-event track invite in Instruqt.</Body>

      {tracksError && <Banner variant="danger">{tracksError}</Banner>}

      <div className={section}>
        <CreateInviteForm
          tracks={tracks}
          tracksLoading={tracksLoading}
          onRefreshTracks={() => loadTracks(environment)}
          onDryRun={handleDryRun}
          onCreate={handleCreate}
          organizationSlug={organizationSlug}
          environment={environment}
          environments={environments}
          internalOnlyEnvironments={internalOnlyEnvironments}
          onEnvironmentChange={handleEnvironmentChange}
        />
      </div>

      <div className={section}>
        {manageInvitesError && <Banner variant="danger">{manageInvitesError}</Banner>}
        {manageInvitesUrl && (
          <Body className={largerText}>
            To view, edit, or delete existing invites, go to{" "}
            <Link href={manageInvitesUrl} target="_blank" rel="noopener noreferrer">
              this Instruqt org's invites page
            </Link>.
          </Body>
        )}
      </div>
    </Fragment>
  );
}
